<template lang="pug">
b-row
  b-col(cols="12")
    div.treatment-wizard-form
      form-wizard.wizard-vertical.mb-3(
        color="#498ceb",
        layout="horizontal",
        :title="null",
        :subtitle="null",
        :finish-button-text="$t('message.submit')",
        :back-button-text="$t('message.previous')",
        :next-button-text="$t('message.next')",
        :hide-buttons="readOnly",
        ref="treatmentForm"
      )
        // Custom footer with buttons
        template(v-slot:footer="slotProps")
          div(class="wizard-footer d-flex justify-content-between align-items-center")
            div
              b-button(
                v-if="!readOnly"
                variant="outline-secondary"
                @click="goToPreviousTab"
                :disabled="slotProps.activeTabIndex === 0"
              )
                | < &nbsp; {{ $t('message.previous') }}

              // Autosave indicator
              b-badge(
                v-if="isAutosaving"
                variant="light-info"
                class="ml-2"
              )
                span.spinner-border.spinner-border-sm.mr-1
                | Guardando borrador...

              b-badge(
                v-else-if="lastAutosaveTime && !readOnly && !isDirty"
                variant="light-success"
                class="ml-2"
              )
                | ✓ Guardado automático {{ formatAutosaveTime(lastAutosaveTime) }}

            div(class="ml-auto d-flex align-items-center")
              b-button(
                v-if="!readOnly && !slotProps.isLastStep"
                variant="primary"
                class="mr-2"
                @click="goToNextTab"
              )
                | {{ $t('message.next') }} &nbsp; >

              b-button(
                v-if="!readOnly"
                variant="success"
                @click="handleSubmit"
              )
                | {{ $t('message.submit') }}

        // PASO 1 - Identificación
        tab-content(:title="$t('Identificación')")
          validation-observer(ref="step1Tab", tag="form")
            b-row
              // ID (auto-generated, read-only)
              b-col(v-if="data.treatmentId", md="12")
                b-form-group(label="ID", label-for="treatmentId")
                  b-form-input(v-model="data.treatmentId", disabled, :style="{ width: data.treatmentId ? '16.66%' : 'auto' }")

            b-row
              // Nombre del tratamiento *
              b-col(md="12")
                b-form-group(label="Nombre del tratamiento *", label-for="nombre")
                  validation-provider(name="Nombre", rules="required", v-slot="{ errors }")
                    b-form-input(v-model="data.nombre", trim, :disabled="readOnly", placeholder="Ingrese el nombre del tratamiento", :style="{ width: data.treatmentId ? '83.33%' : '100%' }")
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Entidad/Empresa responsable *
              b-col(md="12")
                b-form-group(label="Entidad/Empresa responsable *", label-for="empresa_id")
                  validation-provider(name="Empresa", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.empresa_id"
                      label="title"
                      :options="companies"
                      :reduce="empresa => empresa.value"
                      :disabled="readOnly"
                      placeholder="Seleccione empresa"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Rol en el tratamiento *
              b-col(md="12")
                b-form-group(label="Rol en el tratamiento *", label-for="rol")
                  validation-provider(name="Rol", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.rol"
                      label="title"
                      :options="rolPrivacyOptions"
                      :reduce="rol => rol.value",
                      :disabled="readOnly"
                      placeholder="Seleccione rol"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Departamento/Área *
              b-col(md="12")
                b-form-group(label="Departamento/Área *", label-for="departamento_id")
                  validation-provider(name="Departamento", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.departamento_id"
                      label="title"
                      :options="locations"
                      :reduce="location => location.value"
                      :disabled="readOnly"
                      placeholder="Seleccione departamento/área"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Responsable interno
              b-col(md="12")
                b-form-group(label="Responsable interno", label-for="responsable_id")
                  v-select(
                    v-model="data.responsable_id"
                    label="title"
                    :options="workersTotal"
                    :reduce="worker => worker.value"
                    :disabled="readOnly"
                    placeholder="Seleccione responsable"
                    :style="{ width: '50%' }"
                  )

        // PASO 2 - Registro del tratamiento
        tab-content(:title="$t('Registro')")
          validation-observer(ref="step2Tab", tag="form")
            b-row
              // Finalidades *
              b-col(md="12")
                b-form-group(label="Finalidad(es) del tratamiento *", label-for="finalidades")
                  validation-provider(name="Finalidades", rules="required|min:10", v-slot="{ errors }")
                    b-form-textarea(
                      v-model="data.finalidades"
                      :disabled="readOnly"
                      placeholder="Describa las finalidades del tratamiento"
                      rows="3"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Base de legitimación *
              b-col(md="12")
                b-form-group(label="Base de legitimación *", label-for="legitimacion")
                  validation-provider(name="Legitimación", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.legitimacion"
                      :options="legitimacionOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione base legal"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row(v-if="data.legitimacion === 'Consentimiento'")
              // Mecanismo de consentimiento (si legitimación = "Consentimiento")
              b-col(md="12")
                b-form-group(label="Mecanismo de obtención de consentimiento", label-for="mecanismoConsentimiento")
                  b-form-input(v-model="data.mecanismoConsentimiento", :disabled="readOnly", placeholder="Describa el mecanismo", :style="{ width: '50%' }")

            b-row
              // Tipos de datos personales *
              b-col(md="12")
                b-form-group(label="Tipos de datos personales tratados *", label-for="tiposDatos")
                  validation-provider(name="Tipos de datos", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.tiposDatos"
                      :options="tipoDatoOptions"
                      :disabled="readOnly"
                      multiple
                      placeholder="Seleccione tipos de datos"
                    )
                    small(class="text-danger") {{ errors[0] }}

            // Flags prominentes
            b-row.mt-2
              b-col(md="12")
                b-card(:bg-variant="data.hasSensitive ? 'warning' : 'light'" :text-variant="data.hasSensitive ? 'white' : 'dark'" :style="{ width: '33.33%' }")
                  b-form-checkbox(v-model="data.hasSensitive", switch, size="lg")
                    strong ⚠️ ¿Tiene datos sensibles?

            b-row
              b-col(md="12")
                b-card(:bg-variant="data.hasMinors ? 'warning' : 'light'" :text-variant="data.hasMinors ? 'white' : 'dark'" :style="{ width: '33.33%' }")
                  b-form-checkbox(v-model="data.hasMinors", switch, size="lg")
                    strong ⚠️ ¿Tiene datos de NNA?

            b-row
              b-col(md="12")
                b-card(:bg-variant="data.hasBiometric ? 'warning' : 'light'" :text-variant="data.hasBiometric ? 'white' : 'dark'" :style="{ width: '33.33%' }")
                  b-form-checkbox(v-model="data.hasBiometric", switch, size="lg")
                    strong ⚠️ ¿Tiene datos biométricos?

            // Excepción tratamiento sensibles (obligatorio si hasSensitive)
            b-row(v-if="data.hasSensitive", class="mt-2")
              b-col(md="12")
                b-form-group(label="Excepción tratamiento sensibles *", label-for="excepcionSensible")
                  validation-provider(name="Excepción sensibles", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.excepcionSensible"
                      :options="excepcionSensibleOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione excepción"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row(v-if="data.hasSensitive")
              b-col(md="12")
                b-form-group(label="Descripción datos sensibles *", label-for="sensiblesDescripcion")
                  validation-provider(name="Descripción sensibles", rules="required", v-slot="{ errors }")
                    b-form-textarea(
                      v-model="data.sensiblesDescripcion"
                      :disabled="readOnly"
                      placeholder="Describa los datos sensibles"
                      rows="2"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row.mt-2
              // Periodicidad *
              b-col(md="12")
                b-form-group(label="Periodicidad *", label-for="periodicidad")
                  validation-provider(name="Periodicidad", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.periodicidad"
                      :options="periodicidadOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione"
                      :style="{ width: '33.33%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Origen *
              b-col(md="12")
                b-form-group(label="Origen *", label-for="origen")
                  validation-provider(name="Origen", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.origen"
                      :options="origenOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione"
                      :style="{ width: '33.33%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Volumen *
              b-col(md="12")
                b-form-group(label="Volumen estimado *", label-for="volumen")
                  validation-provider(name="Volumen", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.volumen"
                      :options="volumenOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione volumen"
                      :style="{ width: '33.33%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Categoría de interesados
              b-col(md="12")
                b-form-group(label="Categoría de interesados/titulares", label-for="categoriaInteresados")
                  b-form-input(v-model="data.categoriaInteresados", :disabled="readOnly", placeholder="Ej: Clientes, empleados", :style="{ width: '50%' }")

            b-row
              // Medios
              b-col(md="12")
                b-form-group(label="Medios de obtención", label-for="medios")
                  b-form-input(v-model="data.medios", :disabled="readOnly", placeholder="Ej: Formulario web, correo electrónico", :style="{ width: '50%' }")

            b-row
              // Soporte
              b-col(md="12")
                b-form-group(label="Soporte", label-for="soporte")
                  v-select(
                    v-model="data.soporte"
                    :options="soporteOptions"
                    :disabled="readOnly"
                    placeholder="Seleccione"
                    :style="{ width: '33.33%' }"
                  )

            b-row
              // Formato
              b-col(md="12")
                b-form-group(label="Formato", label-for="formato")
                  v-select(
                    v-model="data.formato"
                    :options="formatoOptions"
                    :disabled="readOnly"
                    placeholder="Seleccione"
                    :style="{ width: '33.33%' }"
                  )

            b-row
              // Ámbito *
              b-col(md="12")
                b-form-group(label="Ámbito geográfico *", label-for="ambito")
                  validation-provider(name="Ámbito", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.ambito"
                      label="title"
                      :options="ambitoOptions"
                      :reduce="ambito => ambito.value",
                      :disabled="readOnly"
                      placeholder="Seleccione"
                      :style="{ width: '33.33%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              // Plazo conservación *
              b-col(md="12")
                b-form-group(label="Plazo de conservación *", label-for="plazoConservacion")
                  validation-provider(name="Plazo conservación", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="data.plazoConservacion"
                      :options="conservacionOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione"
                      :style="{ width: '33.33%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

        // PASO 3 - Terceros
        tab-content(:title="$t('Terceros')")
          validation-observer(ref="step3Tab", tag="form")
            b-row
              b-col(md="12")
                h5 Terceros con quienes se comparten datos

            b-row
              b-col(md="12")
                b-form-group(label="Fuente cedente", label-for="fuenteCedente")
                  b-form-input(v-model="data.terceros.fuenteCedente", :disabled="readOnly", placeholder="Nombre de la fuente", :style="{ width: '50%' }")

            b-row
              b-col(md="12")
                b-form-group(label="Regularización de la fuente", label-for="fuenteRegularizacion")
                  b-form-input(v-model="data.terceros.fuenteRegularizacion", :disabled="readOnly", placeholder="Base legal", :style="{ width: '50%' }")

            b-row
              b-col(md="12")
                b-form-group(label="Destinatario", label-for="destinatario")
                  b-form-input(v-model="data.terceros.destinatario", :disabled="readOnly", placeholder="Nombre del destinatario", :style="{ width: '50%' }")

            b-row(v-if="data.terceros.destinatario")
              b-col(md="12")
                b-form-group(label="Finalidad del destinatario", label-for="destinatarioFinalidad")
                  b-form-input(v-model="data.terceros.destinatarioFinalidad", :disabled="readOnly", placeholder="Para qué usa los datos", :style="{ width: '50%' }")

            b-row(v-if="data.terceros.destinatario")
              b-col(md="12")
                b-form-group(label="Legitimación del destinatario *", label-for="destinatarioLegitimacion")
                  validation-provider(name="Legitimación destinatario", :rules="data.terceros.destinatario ? 'required' : ''", v-slot="{ errors }")
                    v-select(
                      v-model="data.terceros.destinatarioLegitimacion"
                      :options="legitimacionOptions"
                      :disabled="readOnly"
                      placeholder="Seleccione legitimación"
                      :style="{ width: '50%' }"
                    )
                    small(class="text-danger") {{ errors[0] }}

        // PASO 4 - Encargados
        tab-content(:title="$t('Encargados')")
          validation-observer(ref="step4Tab", tag="form")
            b-row
              b-col(md="12")
                h5 Encargados de tratamiento

            b-row
              b-col(md="12")
                b-form-group(label="Identificación del encargado", label-for="encargadoIdentificacion")
                  b-form-input(v-model="data.encargados.identificacion", :disabled="readOnly", placeholder="Nombre del encargado", :style="{ width: '50%' }")

            b-row
              b-col(md="12")
                b-form-group(label="Prestación del servicio", label-for="encargadoPrestacion")
                  b-form-input(v-model="data.encargados.prestacion", :disabled="readOnly", placeholder="Actividad encomendada", :style="{ width: '50%' }")

        // PASO 5 - Transferencias internacionales
        tab-content(:title="$t('Transferencias')")
          validation-observer(ref="step5Tab", tag="form")
            b-row
              b-col(md="12")
                h5 Transferencias internacionales

            b-row
              b-col(md="12")
                b-form-group(label="Destinatario internacional", label-for="transferenciaDestinatario")
                  b-form-input(v-model="data.transferencias.destinatario", :disabled="readOnly", placeholder="Organismo receptor", :style="{ width: '50%' }")

            b-row
              b-col(md="12")
                b-form-group(label="País de destino", label-for="transferenciaPais")
                  b-form-input(v-model="data.transferencias.pais", :disabled="readOnly", placeholder="País", :style="{ width: '50%' }")

        // PASO 6 - Seguridad y resguardos
        tab-content(:title="$t('Resguardos')")
          validation-observer(ref="step6Tab", tag="form")
            b-row
              b-col(md="12")
                h5 Medidas de seguridad y resguardos

            b-row
              b-col(md="12")
                b-form-group(label="Medidas en papel", label-for="resguardosPapel")
                  b-form-textarea(
                    v-model="data.resguardos.papel"
                    :disabled="readOnly"
                    placeholder="Describa medidas de seguridad para documentos en papel"
                    rows="4"
                  )

            b-row
              b-col(md="12")
                b-form-group(label="Medidas en sistemas", label-for="resguardosSistemas")
                  b-form-textarea(
                    v-model="data.resguardos.sistemas"
                    :disabled="readOnly"
                    placeholder="Describa medidas de seguridad técnicas"
                    rows="4"
                  )

            b-row
              b-col(md="12")
                b-form-group(label="Observaciones generales", label-for="observaciones")
                  b-form-textarea(
                    v-model="data.observaciones"
                    :disabled="readOnly"
                    placeholder="Observaciones adicionales"
                    rows="3"
                  )

  audit-log-table(
    v-if="(isEdit || readOnly) && data",
    :history="data.history || []",
    :field-translations="{ nombre: 'Nombre del tratamiento', empresa_id: 'Empresa', rol: 'Rol', departamento_id: 'Departamento', responsable_id: 'Responsable', finalidades: 'Finalidades', legitimacion: 'Legitimación', status: 'Estado' }"
  )
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min } from '@validations'
import vSelect from 'vue-select'
import { ref, watch, onMounted, computed } from '@vue/composition-api'
import { BFormCheckbox, BCard, BCardText, BBadge } from "bootstrap-vue"
import useNotifications from '@/composables/useNotifications'
import useAutosave from '@/composables/useAutosave'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { rolPrivacyOptions, ambitoOptions, volumenOptions, legitimacionOptions, tipoDatoOptions, excepcionSensibleOptions, periodicidadOptions, origenOptions, soporteOptions, formatoOptions, conservacionOptions } from '@/constants'
import AuditLogTable from '@/components/AuditLogTable.vue'

export default {
  components: {
    FormWizard,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BFormCheckbox,
    BCard,
    BCardText,
    BBadge,
    AuditLogTable,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    onSaveDraft: {
      type: Function,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { showErrorMessage, showSuccessMessage } = useNotifications()
    const { getWorkersTotalForDropDown, workersTotal, getLocationsForDropDown, locations, getCompaniesForDropDown, companies } = useCommon()
    const treatmentForm = ref(null)

    // Autosave setup (only if saveDraft function is provided and not in readOnly mode)
    const autosaveEnabled = computed(() => !props.readOnly && props.onSaveDraft !== null)

    // Always initialize autosave (it will handle the enabled check internally)
    // Pass a getter function to properly watch prop changes
    const autosaveInstance = useAutosave(
      async (data) => {
        // Only save if autosave is enabled
        if (autosaveEnabled.value && props.onSaveDraft) {
          await props.onSaveDraft(data)
        }
      },
      () => props.data,
      30000 // 30 seconds
    )

    // Refs for validation observers
    const step1Tab = ref(null)
    const step2Tab = ref(null)
    const step3Tab = ref(null)
    const step4Tab = ref(null)
    const step5Tab = ref(null)
    const step6Tab = ref(null)

    // Tabs array for validation
    const tabs = [
      { name: 'step1', ref: step1Tab },
      { name: 'step2', ref: step2Tab },
      { name: 'step3', ref: step3Tab },
      { name: 'step4', ref: step4Tab },
      { name: 'step5', ref: step5Tab },
      { name: 'step6', ref: step6Tab },
    ]

    // Validation function for a specific tab
    const validateTab = (tabRef) => new Promise((resolve, reject) => {
      tabRef.value.validate().then(success => {
        if (success) {
          resolve(true)
        } else {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
          reject(new Error('Validation failed'))
        }
      })
    })

    // Navigate to next tab with validation
    const goToNextTab = async () => {
      const index = treatmentForm.value.activeTabIndex

      try {
        await validateTab(tabs[index].ref)

        // Save draft before advancing using triggerManualSave
        // This cancels any pending autosave timer and performs the save immediately
        if (autosaveEnabled.value && autosaveInstance?.triggerManualSave) {
          try {
            await autosaveInstance.triggerManualSave()
          } catch (error) {
            console.error('Error saving draft on step navigation:', error)
          }
        }

        treatmentForm.value.nextTab()
      } catch (err) {
        // Validation error, don't advance
      }
    }

    // Navigate to previous tab
    const goToPreviousTab = () => {
      treatmentForm.value.prevTab()
    }

    // Validate all tabs before submit
    const validateAllTabs = async () => {
      const wizard = treatmentForm.value

      for (let i = 0; i < wizard.tabs.length; i++) {
        try {
          await validateTab(tabs[i].ref)
        } catch (error) {
          // If validation fails, set active tab to the failed one
          if (i !== wizard.activeTabIndex) {
            wizard.changeTab(wizard.activeTabIndex, i)
          }
          throw error
        }
      }
    }

    // Handle form submission
    const handleSubmit = async () => {
      try {
        await validateAllTabs()
        emit('submit', props.data)
      } catch (error) {
        // Validation failed, don't submit
      }
    }

    // Format autosave time for display
    const formatAutosaveTime = (time) => {
      if (!time) return ''
      const now = new Date()
      const diff = Math.floor((now - time) / 1000) // seconds

      if (diff < 60) return 'hace un momento'
      if (diff < 120) return 'hace 1 minuto'
      if (diff < 3600) return `hace ${Math.floor(diff / 60)} minutos`
      return time.toLocaleTimeString()
    }

    onMounted(() => {
      getWorkersTotalForDropDown()
      getLocationsForDropDown()
      getCompaniesForDropDown()
    })

    return {
      treatmentForm,
      step1Tab,
      step2Tab,
      step3Tab,
      step4Tab,
      step5Tab,
      step6Tab,
      rolPrivacyOptions,
      legitimacionOptions,
      tipoDatoOptions,
      excepcionSensibleOptions,
      periodicidadOptions,
      origenOptions,
      volumenOptions,
      soporteOptions,
      formatoOptions,
      ambitoOptions,
      conservacionOptions,
      companies,
      workersTotal,
      locations,
      goToNextTab,
      goToPreviousTab,
      handleSubmit,
      required,
      min,
      isAutosaving: autosaveInstance?.isAutosaving,
      lastAutosaveTime: autosaveInstance?.lastAutosaveTime,
      isDirty: autosaveInstance?.isDirty,
      formatAutosaveTime,
    }
  },
}
</script>

<style lang="scss" scoped>
.wizard-footer {
  padding: 1rem;
  border-top: 1px solid #ebe9f1;
}

.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 500;
  }
}

::v-deep .wizard-icon-circle {
  background-color: #498ceb !important;
  border-color: #498ceb !important;
}

// Mejorar contraste de inputs con fondo gris más oscuro
::v-deep .form-control {
  background-color: #e9ecef !important;
}

::v-deep .form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep textarea.form-control {
  background-color: #e9ecef !important;
}

::v-deep textarea.form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep input.form-control {
  background-color: #e9ecef !important;
}

::v-deep input.form-control:disabled {
  background-color: #c6c6c6 !important;
}

// Para v-select
::v-deep .vs__dropdown-toggle {
  background-color: #e9ecef !important;
}

::v-deep .v-select.vs--disabled .vs__dropdown-toggle {
  background-color: #c6c6c6 !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-wizard.scss";

// Estilos globales para este componente específico (sin scoped para penetrar en v-select)
.treatment-wizard-form {
  .v-select .vs__dropdown-toggle {
    background-color: #e9ecef !important;
  }

  .v-select.vs--disabled .vs__dropdown-toggle {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  // Inputs disabled con más especificidad
  input.form-control[disabled],
  input.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  textarea.form-control[disabled],
  textarea.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  .form-control[disabled],
  .form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }
}
</style>
